import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { BookMinus, Send } from "lucide-react";
import { toast } from "react-toastify";
import { setTitle } from "../../../helpers/MetaTag";
import { resetStep } from "../../../redux/slice/stepSlice";
import { clearSketch } from "../../../redux/slice/sketchSlice";
import useOpenBook from "../../../hooks/useOpenBook";
import { loadUserData } from "../../../util/navigationUtils";
import Button from "../../../components/ui/Button";
import BottomBar from "../../../components/ui/BottomBar";
import WizardShell from "../../../components/ui/WizardShell";
import ErrorState from "../../../components/ui/ErrorState";
import Popup from "../../../components/ui/Popup";
import StatusLabel from "../../../components/ui/StatusLabel";
import Skeleton, { SkeletonGroup } from "../../../components/ui/Skeleton";
import AddressCard from "../../../components/gift/AddressCard";
import {
  getGift,
  getMyGifts,
  giftIdFromSearch,
  giftPagePath,
  giftPlanPath,
  giftWelcomePath,
  newGiftBookPath,
  removeGiftBook,
  reopenGiftBook,
  submitGift,
} from "../../../services/giftService";
import CoverPreview from "../../../components/builder/CoverPreview";

/**
 * /gift/books?id=... Gift 3 (docs/design/HANDOFF.md: GiftBooks, TabGiftBooks,
 * DeskGiftBooks): one row per book the split calls for (Ready, In progress or
 * Not started), the mailing address with Edit, and "Send my books to print",
 * which stays off until every book is ready. The copies are fixed by the
 * split; "Change how your N books are split" goes back to Gift 2.
 *
 * The server owns the rules (every book gets at least one copy, the copies
 * always add up, nothing prints until every book is finished).
 */
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** The rows to show: the split's slots, or (gifts made before the split existed) the books plus one open row. */
function rowsFor(gift) {
  if (gift.slots) return gift.slots;
  const rows = gift.books.map((b, i) => ({ slot: i, copies: b.copies, book: b }));
  if (gift.canAddBook) {
    const used = gift.books.reduce((s, b) => s + b.copies, 0);
    rows.push({ slot: rows.length, copies: Math.max(1, gift.quantity - used), book: null });
  }
  return rows;
}

const GiftBooks = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const openBook = useOpenBook();
  const myBooks = useSelector((state) => state.profile.myBooks);
  const sessionUserId = useSelector((state) => state?.authProvider?.session?.user?._id);

  const giftId = giftIdFromSearch(location.search, "id");
  const [gift, setGift] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null); // { title, body, label, tone, onConfirm }

  useEffect(() => {
    setTitle("Your gift | MemoryDoodle");
  }, []);

  // Opened without an id (from My Books): use the newest open gift.
  useEffect(() => {
    if (giftId) return;
    getMyGifts().then((res) => {
      const list = res?.success ? res.data || [] : [];
      const open = list.find((g) => g.open) || list[0];
      if (open) navigate(giftPagePath(open.giftId), { replace: true });
      else setLoadError("We couldn't find a gift on your account.");
    });
  }, [giftId, navigate]);

  const load = useCallback(async () => {
    if (!giftId) return;
    setLoadError(null);
    const res = await getGift(giftId);
    if (res?.success) setGift(res.data);
    else setLoadError(res?.message || "We couldn't load your gift.");
  }, [giftId]);
  useEffect(() => {
    load();
  }, [load]);

  // Sent gifts go to the done page; nothing started yet goes to the welcome or split screen.
  useEffect(() => {
    if (!gift) return;
    if (gift.submitted) navigate(`/gift/done?id=${encodeURIComponent(gift.giftId)}`, { replace: true });
    else if (gift.open && !gift.books.length) navigate(gift.plan && gift.quantity > 1 ? giftPlanPath(gift.giftId) : giftWelcomePath(gift.giftId), { replace: true });
  }, [gift, navigate]);

  const coverFor = (bookId) => {
    const book = (myBooks || []).find((b) => String(b._id) === String(bookId));
    return book?.coverImageThumbnail || book?.coverImage || null;
  };

  const act = async (fn, { success } = {}) => {
    if (busy) return null;
    setBusy(true);
    try {
      const res = await fn();
      if (res?.success) {
        if (res.data && res.data.giftId) setGift(res.data);
        if (success) toast.success(success);
        return res;
      }
      toast.error(res?.message || "Something went wrong. Please try again.");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const startBook = () => {
    // Same reset My Books does before a new book, so step 1 opens empty.
    dispatch(resetStep());
    dispatch(clearSketch());
    navigate(newGiftBookPath(gift.giftId));
  };
  const editBook = async (book) => {
    if (book.finalized) {
      const res = await act(() => reopenGiftBook(gift.giftId, book._id));
      if (!res) return;
    }
    openBook(book._id);
  };
  const removeBook = (book, n) => {
    setConfirm({
      title: `Take Book ${n} out of your gift?`,
      body: "It stays in My Books. You can make a different book for this spot.",
      label: "Take it out",
      tone: "red",
      onConfirm: () => act(() => removeGiftBook(gift.giftId, book._id), { success: "Book taken out of your gift." }),
    });
  };
  const send = () => {
    const them = gift.quantity > 1 ? "books" : "book";
    setConfirm({
      title: `Send your ${them} to print?`,
      body: "You won't be able to make changes after this.",
      label: "Send to print",
      tone: "rose",
      onConfirm: sendNow,
    });
  };
  const sendNow = async () => {
    const res = await act(() => submitGift(gift.giftId));
    if (!res) return;
    // The app's copy of the books still has these as unfinished; left that
    // way, My Books "resumes" the book they just sent instead of listing it.
    try {
      await loadUserData(sessionUserId, dispatch, false);
    } catch (err) {
      console.error("Reloading books after sending the gift failed:", err);
    }
    navigate(`/gift/done?id=${encodeURIComponent(gift.giftId)}`, { replace: true });
  };

  if (loadError) {
    return (
      <WizardShell plain>
        <ErrorState title="We couldn't open your gift" onRetry={giftId ? load : null}>
          {loadError}
        </ErrorState>
      </WizardShell>
    );
  }
  if (!gift) {
    return (
      <WizardShell plain>
        <SkeletonGroup label="Loading your gift" className="flex flex-col gap-4">
          <Skeleton shape="line" className="h-9 w-2/3" />
          <Skeleton shape="block" className="h-24" />
          <Skeleton shape="block" className="h-24" />
          <Skeleton shape="block" className="h-32" />
        </SkeletonGroup>
      </WizardShell>
    );
  }

  const giver = gift.giftFrom || "Someone special";
  const many = gift.quantity > 1;

  if (gift.submitted) return null;
  if (!gift.open) {
    return (
      <WizardShell plain>
        <ErrorState title="This gift is no longer available">If you think that's a mistake, just reply to your gift email and we'll help.</ErrorState>
      </WizardShell>
    );
  }
  // Nothing started yet: the welcome and split screens come first.
  if (!gift.books.length) return null;

  const rows = rowsFor(gift);
  const bookCount = rows.length;
  const readyCount = rows.filter((r) => r.book?.ready).length;
  const notReady = rows.map((r, i) => (r.book?.ready ? null : i + 1)).filter(Boolean);
  const firstEmpty = rows.findIndex((r) => !r.book);
  const sendNote = gift.readyToSubmit
    ? many
      ? `We print all ${bookCount} ${bookCount === 1 ? "book" : "books"} together and mail them in one package.`
      : "We print your book and mail it as soon as you send it."
    : `Finish ${notReady.map((n) => `Book ${n}`).join(notReady.length === 2 ? " and " : ", ").replace(/, (Book \d+)$/, " and $1")} to send your ${many ? "books" : "book"} to print.`;

  const primary = (
    <Button size="lg" fullWidth onClick={send} disabled={busy || !gift.readyToSubmit} icon={<Send className="h-5 w-5" aria-hidden="true" />}>
      Send my {many ? "books" : "book"} to print
    </Button>
  );

  return (
    <WizardShell plain bottomBar={<BottomBar primary={primary} note={sendNote} />}>
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:gap-8">
      <div className="flex min-w-0 flex-col gap-5 lg:flex-[999_1_520px]">
        <div className="flex flex-col gap-1.5">
          <h1 className="m-0 font-heading text-[30px] font-normal leading-[1.1] text-md-dark md:text-[40px] lg:text-[44px]">Your gift from {giver}</h1>
          <p className="m-0 text-[15px] leading-snug text-md-muted md:text-[17px]">
            {many ? `${gift.quantity} printed coloring books. Finish each one, then send them all to print together.` : "A printed coloring book made from your own photos. Finish it, then send it to print."}
          </p>
        </div>

        <section aria-labelledby="gift-books-heading" className="flex flex-col gap-3">
          <div className="flex items-baseline justify-between gap-3">
            <h2 id="gift-books-heading" className="m-0 text-lg font-bold text-md-dark">
              {bookCount === 1 ? "Your book" : "Your books"}
            </h2>
            {bookCount > 1 ? (
              <span className="text-sm font-semibold text-md-muted">
                {readyCount} of {bookCount} ready
              </span>
            ) : null}
          </div>
          <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
            {rows.map((row, i) => {
              const book = row.book;
              const n = i + 1;
              const status = !book ? "ordered" : book.ready ? "ready" : "progress";
              const statusText = !book ? "Not started" : book.ready ? "Ready" : "In progress";
              const cover = book ? coverFor(book._id) : null;
              return (
                // Phone: the buttons sit under the words on their own row, so a long title isn't squeezed.
                <li key={row.slot ?? i} className="flex flex-wrap items-center gap-3.5 rounded-[14px] border border-md-pink-light-1 bg-white p-3.5 md:flex-nowrap">
                  {book ? (
                    <div aria-hidden="true" className="w-14 flex-shrink-0 overflow-hidden rounded-md">
                      <CoverPreview imageUrl={cover} title={book.title} />
                    </div>
                  ) : (
                    <span aria-hidden="true" className="h-[72px] w-14 flex-shrink-0 rounded-md border-2 border-dashed border-md-control bg-[#FBF9F5]" />
                  )}
                  <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                    <div className="flex flex-col gap-0.5">
                      <span className="truncate text-base font-bold text-md-dark">{book?.title ? book.title : `Book ${n}`}</span>
                      <span className="text-sm text-md-muted">
                        {plural(row.copies, "copy", "copies")}
                        {book?.title ? ` · Book ${n}` : ""}
                      </span>
                    </div>
                    <StatusLabel status={status} className="w-fit">
                      {statusText}
                    </StatusLabel>
                  </div>
                  <div className="flex w-full flex-shrink-0 flex-row-reverse items-center justify-between gap-3 md:w-auto md:flex-col md:items-end md:gap-1">
                    {book ? (
                      <Button variant={book.ready ? "secondary" : "primary"} size="sm" onClick={() => editBook(book)} disabled={busy}>
                        {book.ready ? "Make changes" : "Keep working"}
                      </Button>
                    ) : i === firstEmpty ? (
                      <Button size="sm" onClick={startBook} disabled={busy || !gift.canAddBook}>
                        Start book {n}
                      </Button>
                    ) : null}
                    {book && bookCount > 1 ? (
                      // TODO(design): taking a book out of a gift isn't in the designs (docs/design/GAPS.md).
                      <button type="button" onClick={() => removeBook(book, n)} disabled={busy} className="min-h-[32px] px-1 text-xs font-semibold text-md-muted underline underline-offset-2">
                        Take out of gift
                      </button>
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
          {many ? (
            <Button variant="text" to={giftPlanPath(gift.giftId)} className="self-start !px-1">
              Change how your {gift.quantity} books are split
            </Button>
          ) : null}
        </section>

        {gift.addressNeeded ? (
          <AddressCard giftId={gift.giftId} address={gift.address} single={!many} note={many ? "All books ship together in one package." : null} onSaved={(data) => setGift(data)} className="lg:hidden" />
        ) : null}
      </div>

      <aside className="hidden lg:flex lg:max-w-[400px] lg:flex-[1_1_320px] lg:flex-col lg:gap-4">
        {gift.addressNeeded ? (
          <AddressCard giftId={gift.giftId} address={gift.address} single={!many} note={many ? "All books ship together in one package." : null} onSaved={(data) => setGift(data)} />
        ) : null}
        <div>
          {primary}
          <p className="mt-2 text-center text-[13px] text-md-muted">{sendNote}</p>
        </div>
      </aside>
      </div>

      <Popup
        open={!!confirm}
        onClose={() => setConfirm(null)}
        tone={confirm?.tone || "rose"}
        alert={confirm?.tone === "red"}
        icon={confirm?.tone === "red" ? <BookMinus className="h-6 w-6" aria-hidden="true" /> : <Send className="h-6 w-6" aria-hidden="true" />}
        title={confirm?.title}
        actions={[
          <Button
            key="go"
            variant={confirm?.tone === "red" ? "destructive" : "primary"}
            onClick={() => {
              const run = confirm?.onConfirm;
              setConfirm(null);
              if (run) run();
            }}
          >
            {confirm?.label || "OK"}
          </Button>,
          <Button key="cancel" variant="secondary" onClick={() => setConfirm(null)}>
            Cancel
          </Button>,
        ]}
      >
        {confirm?.body}
      </Popup>
    </WizardShell>
  );
};

export default GiftBooks;
